import { useState, useEffect, useCallback } from "react";
import { api } from "@/lib/api";
import { useTheme } from "@/lib/ThemeContext";
import { Settings, Users, FolderOpen, ListTodo, Timer, ChevronDown, ChevronUp, ToggleLeft, ToggleRight } from "lucide-react";

export default function ShadowOwnerPanel() {
  const [open, setOpen] = useState(false);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const { tokens: t } = useTheme();

  const fetchStats = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.get("/admin/owner-stats");
      setStats(data);
    } catch {
      setStats(null);
    }
    setLoading(false);
  }, []);

  useEffect(() => { if (open && !stats) fetchStats(); }, [open, stats, fetchStats]);

  const toggleFlag = async (key) => {
    try {
      const result = await api.put(`/admin/owner-flags/${key}`);
      setStats(prev => ({
        ...prev,
        feature_flags: prev.feature_flags.map(f => f.key === key ? { ...f, status: result.status } : f),
      }));
    } catch { /* silently fail */ }
  };

  return (
    <div className="fixed bottom-4 left-4 z-[200]" data-testid="shadow-owner-panel">
      <button
        onClick={() => setOpen(o => !o)}
        className="w-10 h-10 rounded-full flex items-center justify-center shadow-lg transition-all hover:scale-110"
        style={{
          background: "linear-gradient(135deg, #7c3aed, #4f46e5)",
          color: "#fff",
          border: "2px solid rgba(255,255,255,0.15)",
        }}
        data-testid="shadow-owner-toggle"
        title="Owner Panel"
      >
        <Settings className="h-4 w-4" />
      </button>

      {open && (
        <div className="absolute bottom-12 left-0 w-80 rounded-xl shadow-2xl overflow-hidden"
          style={{ background: "#111118", border: "1px solid rgba(124,58,237,0.3)" }}
          data-testid="shadow-owner-drawer">
          <div className="px-4 py-3 flex items-center justify-between"
            style={{ background: "linear-gradient(135deg, #7c3aed22, #4f46e522)", borderBottom: "1px solid rgba(124,58,237,0.2)" }}>
            <span className="text-xs font-bold uppercase tracking-wider" style={{ color: "#a78bfa" }}>Owner Panel</span>
            <button onClick={() => setOpen(false)} className="p-1 rounded hover:bg-white/10">
              <ChevronDown className="h-3 w-3" style={{ color: "#a78bfa" }} />
            </button>
          </div>

          {loading ? (
            <div className="p-4 text-center">
              <div className="w-5 h-5 border-2 border-purple-400 border-t-transparent rounded-full animate-spin mx-auto" />
            </div>
          ) : stats ? (
            <div className="p-3 space-y-3 max-h-80 overflow-y-auto">
              {/* Stats grid */}
              <div className="grid grid-cols-2 gap-2">
                {[
                  { icon: Users, label: "Users", value: stats.total_users, color: "#3b82f6" },
                  { icon: FolderOpen, label: "Projects", value: stats.total_projects, color: "#8b5cf6" },
                  { icon: ListTodo, label: "Tasks", value: stats.total_tasks, color: "#10b981" },
                  { icon: Timer, label: "Sessions", value: stats.total_sessions, color: "#f59e0b" },
                ].map(s => (
                  <div key={s.label} className="rounded-lg px-3 py-2" style={{ background: `${s.color}10`, border: `1px solid ${s.color}20` }}>
                    <div className="flex items-center gap-1.5 mb-0.5">
                      <s.icon className="h-3 w-3" style={{ color: s.color }} />
                      <span className="text-[9px] font-medium uppercase tracking-wider" style={{ color: `${s.color}90` }}>{s.label}</span>
                    </div>
                    <span className="text-lg font-bold tabular-nums" style={{ color: s.color, fontFamily: "'JetBrains Mono', monospace" }}>
                      {s.value}
                    </span>
                  </div>
                ))}
              </div>

              {/* Feature flags */}
              {stats.feature_flags?.length > 0 && (
                <div>
                  <span className="text-[9px] font-bold uppercase tracking-wider block mb-1.5" style={{ color: "#a78bfa" }}>Feature Flags</span>
                  <div className="space-y-1">
                    {stats.feature_flags.map(f => (
                      <button key={f.key} onClick={() => toggleFlag(f.key)}
                        className="flex items-center gap-2 w-full px-2 py-1.5 rounded text-left hover:bg-white/5 transition-colors"
                        data-testid={`flag-toggle-${f.key}`}>
                        {f.status !== "disabled"
                          ? <ToggleRight className="h-4 w-4 shrink-0 text-green-400" />
                          : <ToggleLeft className="h-4 w-4 shrink-0 text-gray-500" />}
                        <span className="text-xs flex-1 truncate" style={{ color: f.status !== "disabled" ? "#d1d5db" : "#6b7280" }}>
                          {f.key}
                        </span>
                        <span className="text-[9px] px-1.5 rounded" style={{
                          background: f.status !== "disabled" ? "#10b98120" : "#6b728020",
                          color: f.status !== "disabled" ? "#10b981" : "#6b7280",
                        }}>
                          {f.status}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Recent signups */}
              <div>
                <span className="text-[9px] font-bold uppercase tracking-wider block mb-1.5" style={{ color: "#a78bfa" }}>Recent Signups</span>
                <div className="space-y-0.5">
                  {stats.recent_signups?.slice(0, 5).map((u, i) => (
                    <div key={i} className="flex items-center gap-2 px-2 py-1 rounded text-xs" style={{ color: "#9ca3af" }}>
                      <div className="w-5 h-5 rounded-full flex items-center justify-center text-[8px] font-bold shrink-0"
                        style={{ background: "#7c3aed20", color: "#a78bfa" }}>
                        {(u.name || u.email || "?")[0].toUpperCase()}
                      </div>
                      <span className="flex-1 truncate">{u.email}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Admin link */}
              <a href="/admin" className="block text-center text-[10px] font-semibold px-3 py-2 rounded-lg transition-colors hover:bg-purple-500/10"
                style={{ color: "#a78bfa", border: "1px solid #7c3aed30" }}
                data-testid="admin-link">
                Open Admin Dashboard
              </a>
            </div>
          ) : (
            <div className="p-4 text-center text-xs" style={{ color: "#6b7280" }}>Failed to load stats</div>
          )}
        </div>
      )}
    </div>
  );
}
